import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { useUser } from '../../components/UserContext';
import { useClients, useContracts } from '../../contexts/DataContext';
import { FianzaView } from '../../components/FianzaView';
import { useBandejaEmails } from '../../hooks/useBandejaEmails';
import { InboxIcon, ShieldCheckIcon, EnvelopeIcon, DocumentTextIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const TEMPLATE_LABELS = {
  'solicitud-expediente': 'Solicitud de documentos',
  'pendiente-solicitud': 'Pendientes por entregar',
  'fianza-emitida': 'Fianza emitida',
  'welcome-client': 'Bienvenida al portal',
  'acceso-cliente': 'Nuevo acceso',
};

function labelFor(row) {
  if (row.templateKey === 'solicitud-expediente' && row.kind === 'recordatorio') {
    return 'Recordatorio de documentos';
  }
  return TEMPLATE_LABELS[row.templateKey] || 'Correo';
}

function iconFor(row) {
  if (row.templateKey === 'fianza-emitida') return ShieldCheckIcon;
  if (row.templateKey === 'solicitud-expediente') return DocumentTextIcon;
  if (row.templateKey === 'pendiente-solicitud') return ClipboardDocumentListIcon;
  return EnvelopeIcon;
}

function formatFecha(sentAt) {
  const d = sentAt?.toDate?.();
  if (!d) return '';
  return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
}

function relativeFecha(sentAt) {
  const ms = sentAt?.toMillis?.();
  if (!ms) return '';
  const days = Math.floor((Date.now() - ms) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Hoy';
  if (days === 1) return 'Ayer';
  if (days < 30) return `Hace ${days} días`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'Hace 1 mes' : `Hace ${months} meses`;
}

/**
 * Bandeja de entrada del portal: los correos que la agencia envió sobre las
 * empresas del usuario — fianzas emitidas, solicitudes, recordatorios —
 * más reciente primero. Correos posteriores a usuarios/{uid}.bandejaVistaAt
 * salen como nuevos; abrir la página actualiza el sello.
 */
const ClientPortalBandeja = () => {
  const { user, userData } = useUser();
  const { clientsById } = useClients();
  const { contractsList } = useContracts();
  const emails = useBandejaEmails(); // null while loading
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);
  // Unread cutoff captured once on entry, BEFORE stamping the visit, so the
  // badges stay visible during this visit and clear on the next one.
  const [lastSeen, setLastSeen] = useState(null);

  useEffect(() => {
    if (lastSeen !== null || !userData || !user?.uid) return;
    setLastSeen(userData.bandejaVistaAt || 0);
    updateDoc(doc(db, 'usuarios', user.uid), { bandejaVistaAt: Date.now() })
      .catch((e) => console.error('Failed to stamp bandejaVistaAt:', e));
  }, [lastSeen, userData, user?.uid]);

  const openFianza = (row) => {
    if (!row.fianzaId || !row.contratoId) return;
    const contrato = contractsList.find((c) => c.id === row.contratoId);
    const fianza = (contrato?.fianzas || []).find((f) => f.id === row.fianzaId);
    if (!fianza) return;
    setSelectedFianza({ ...fianza, contratoId: contrato.id, clientId: contrato.clientId });
    setIsFianzaViewOpen(true);
  };

  // Single loading gate: nothing renders until the emails AND the unread
  // cutoff are both ready, so the list never repaints right after mounting.
  if (emails === null || lastSeen === null) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <Heading level={1} className="text-2xl font-bold text-azul-surelia mb-1">
        Bandeja de entrada
      </Heading>
      <Text className="text-sm text-zinc-500 mb-6">
        Correos recientes de tu agencia
      </Text>

      {emails.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-zinc-400">
          <InboxIcon className="w-10 h-10" />
          <Text className="text-sm text-zinc-500">Aún no has recibido correos.</Text>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-zinc-200 divide-y divide-zinc-100 shadow-sm">
          {emails.map((row) => {
            const sentMs = row.sentAt?.toMillis?.() || 0;
            const isNew = sentMs > lastSeen;
            const clienteNombre = clientsById[row.clientId]?.nombreEmpresa || '';
            const Icon = iconFor(row);
            const clickable = Boolean(row.fianzaId && row.contratoId);
            return (
              <div
                key={row.id}
                onClick={() => clickable && openFianza(row)}
                className={`flex items-start gap-3 px-4 py-3.5 transition-colors ${
                  clickable ? 'cursor-pointer hover:bg-azul-surelia/5' : ''
                } ${isNew ? 'bg-verde-lima/5' : ''}`}
              >
                <div className="mt-0.5 p-2 rounded-full flex-shrink-0 bg-azul-surelia/10 text-azul-surelia">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Text className="text-sm font-semibold text-zinc-800">{labelFor(row)}</Text>
                    {isNew && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-verde-lima text-azul-surelia">
                        Nuevo
                      </span>
                    )}
                  </div>
                  {row.subject && (
                    <Text className="text-xs text-zinc-600 break-words mt-0.5">{row.subject}</Text>
                  )}
                  <Text className="text-xs text-zinc-400 break-words">
                    {clienteNombre}{clienteNombre && row.to ? ' — ' : ''}{row.to ? `para ${row.to}` : ''}
                  </Text>
                </div>
                <div className="flex-shrink-0 text-right">
                  <Text className="text-xs font-medium text-zinc-600">{relativeFecha(row.sentAt)}</Text>
                  <Text className="text-[11px] text-zinc-400">{formatFecha(row.sentAt)}</Text>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedFianza && (
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={() => { setIsFianzaViewOpen(false); setSelectedFianza(null); }}
          fianza={selectedFianza}
          clientData={clientsById[selectedFianza.clientId]}
          contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId)}
        />
      )}
    </div>
  );
};

export default ClientPortalBandeja;
